import { Component, DestroyRef, inject, model, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PropertyModel } from '../../../../core/models/data/property.model';
import { PropertiesService } from '../../../../core/services/property.service';
import { ProfileConstants } from '../../../../core/constants/profile.constants';
import { PropertyProfileHeaderComponent } from "../../components/property-profile-header/property-profile-header.component";
import { PropertyDataComponent } from '../../components/property-profile-data/property-profile-data.component';
import { PropertyContactsComponent } from '../../components/property-profile-contacts/property-profile-contacts.component';
import { PropertyProfileLogsComponent } from "../../components/property-profile-logs/property-profile-logs.component";
import { PropertyInvoicingComponent } from "../../components/property-profile-invoicing/property-profile-invoicing.component";
import { PaymentHistoryListComponent } from "../../../balances/components/payment-history-list/payment-history-list.component";
import { PropertyFormModalComponent } from "../../components/property-form-modal/property-form-modal.component";
import { PropertiesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseProfilePageClass } from '../../../../core/base-classes/base-profile-page-class';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { FormState } from '../../../../core/enums/forms.enums';
import { PropertyMemberTypes } from '../../../../core/enums/data.enums';
import { ModalActions, ModalTypes, PaymentHistoryListViewLocations } from '../../../../core/enums/base.enums';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { PropertyUtilities } from '../../../../core/utilities/property.utilities';
@Component({
  selector: 'ms-property-profile',
  imports: [CommonModule, FontAwesomeModule, PropertyProfileHeaderComponent, PropertyDataComponent, PropertyContactsComponent, PropertyProfileLogsComponent, PropertyInvoicingComponent, PaymentHistoryListComponent, PropertyFormModalComponent, ConfirmationModalComponent, InfoModalComponent],
  templateUrl: './property-profile.page.html',
  // Scopes every permission check beneath this page to the property's own organization. A property
  // profile is reachable for any organization the viewer belongs to without changing the active one
  // (DEV-2643).
  providers: [ProfileOrganizationScopeService]
})
export class PropertyProfilePage extends BaseProfilePageClass implements OnInit {

  public property = model<PropertyModel | undefined>(undefined);
  public PaymentHistoryListViewLocations = PaymentHistoryListViewLocations;
  public isLoading = signal<boolean>(false);
  public propertyId: number | undefined = undefined;
  public editFormState = signal<FormState>(FormState.Update);

  public isSubmitting = false;
  public confirmationConfirmFunction: Function = () => {};
  public confirmationCancelFunction: Function = () => {};
  private pendingStatusActivation = true;

  @ViewChild(PropertyFormModalComponent) propertyFormModalRef!: PropertyFormModalComponent;
  @ViewChild('propertyProfileConfirmationModalRef') confirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('propertyProfileInfoModalRef') infoModalRef!: InfoModalComponent;

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private propertiesService = inject(PropertiesService);
  private destroyRef = inject(DestroyRef);
  private bootstrapService = inject(BootstrapService);
  constructor() {
    super();
  }

  ngOnInit(): void {
    this.navItems.set(ProfileConstants.PROPERTY_PROFILE_NAV_ITEMS());
    this.propertyId = +(this.activatedRoute.snapshot.paramMap.get('propertyId') ?? NaN) || undefined;

    // Deep link to a tab with ?tab=, matching the member profile (DEV-2044). Must follow navItems.set.
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.setTabFromUrl(params.get('tab') ?? ''));

    this.loadPropertyProfile();
  }

  public onEditProperty(): void {
    this.propertyFormModalRef.openPropertyModal();
  }

  public onEditSuccess(): void {
    this.loadPropertyProfile();
  }

  //#region Activate / Deactivate / Delete
  public confirmPropertyDeactivation(): void {
    this.openStatusConfirmation(false);
  }

  public confirmPropertyReactivation(): void {
    this.openStatusConfirmation(true);
  }

  private openStatusConfirmation(activate: boolean): void {
    const current = this.property();
    if (!current) return;

    this.pendingStatusActivation = activate;
    this.confirmationConfirmFunction = this.confirmedStatusChange;
    this.confirmationCancelFunction = this.cancelConfirmationModal;

    const message = PropertiesMessagingConstants.PROPERTIES_UPDATE_STATUS_CONFIRMATION().replaceValuesWithObject({
      actionText: activate ? 'reactivate' : 'deactivate',
      propertyLabel: PropertyUtilities.getPropertyLabel(current)
    });

    this.confirmationModalRef.modalType.set(ModalTypes.Question);
    this.confirmationModalRef.message.set(message);
    this.confirmationModalRef.enableNotes.set(true);
    this.confirmationModalRef.isNoteRequired.set(false);
    this.confirmationModalRef.enableTypeToConfirm.set(false);

    this.bootstrapService.manageModal('#propertyProfileConfirmationModal', ModalActions.Show);
  }

  public async confirmedStatusChange(note: string | undefined): Promise<void> {
    const current = this.property();
    if (!current) return;

    this.isSubmitting = true;
    try {
      const response = await this.propertiesService.updatePropertyStatus(current.propertyId, this.pendingStatusActivation, note);

      if (response.success === false) {
        this.infoModalRef.modalType.set(ModalTypes.Failure);
      } else {
        this.infoModalRef.modalType.set(ModalTypes.Success);
        this.loadPropertyProfile();
      }

      this.infoModalRef.message.set(response.message);
      this.bootstrapService.manageModal('#propertyProfileInfoModal', ModalActions.Show);
      this.bootstrapService.manageModal('#propertyProfileConfirmationModal', ModalActions.Hide);
    } finally {
      this.isSubmitting = false;
    }
  }

  public confirmPropertyDeletion(): void {
    const current = this.property();
    if (!current) return;

    this.confirmationConfirmFunction = this.confirmedPropertyDeletion;
    this.confirmationCancelFunction = this.cancelConfirmationModal;

    const message = PropertiesMessagingConstants.PROPERTIES_DELETE_CONFIRMATION().replaceValuesWithObject({
      propertyLabel: PropertyUtilities.getPropertyLabel(current)
    });

    this.confirmationModalRef.modalType.set(ModalTypes.Question);
    this.confirmationModalRef.message.set(message);
    this.confirmationModalRef.enableNotes.set(true);
    this.confirmationModalRef.isNoteRequired.set(false);
    this.confirmationModalRef.enableTypeToConfirm.set(false);

    this.bootstrapService.manageModal('#propertyProfileConfirmationModal', ModalActions.Show);
  }

  public async confirmedPropertyDeletion(note: string | undefined): Promise<void> {
    const current = this.property();
    if (!current) return;

    this.isSubmitting = true;
    try {
      const response = await this.propertiesService.deleteProperty(current.propertyId, note);

      if (response.success === false) {
        this.infoModalRef.modalType.set(ModalTypes.Failure);
      } else {
        this.infoModalRef.modalType.set(ModalTypes.Success);
        this.loadPropertyProfile();
      }

      this.infoModalRef.message.set(response.message);
      this.bootstrapService.manageModal('#propertyProfileInfoModal', ModalActions.Show);
      this.bootstrapService.manageModal('#propertyProfileConfirmationModal', ModalActions.Hide);
    } finally {
      this.isSubmitting = false;
    }
  }

  public cancelConfirmationModal(): void {
    this.bootstrapService.manageModal('#propertyProfileConfirmationModal', ModalActions.Hide);
  }
  //#endregion Activate / Deactivate / Delete

  private async loadPropertyProfile(): Promise<void> {
    this.isLoading.set(true);
    this.property.set(undefined);
    this.isEntityOwner.set(false);
    // Cleared before the fetch so org-level permission checks deny while we don't yet know which
    // organization this property belongs to, rather than falling back to the active one.
    this.profileOrganizationScope?.clear();

    try {
      let response = await this.propertiesService.getPropertyById(Number(this.propertyId), true);

      if (response?.data) {
        this.profileOrganizationScope?.set(response.data.organizationId);
        this.property.set(response.data);
        this.isEntityOwner.set(this.checkPropertyOwnership(response.data));
      } else {
        this.router.navigate(['system', '404']);
        return;
      }
    } catch (error) {
      console.error(PropertiesMessagingConstants.PROPERTIES_ERROR_LOADING_PROPERTY_PROFILE(), error);
      this.router.navigate(['system', '404']);
      return;
    } finally {
      this.isLoading.set(false);
    }
  }

  private checkPropertyOwnership(property: PropertyModel): boolean {
    const authState = this.authorizationStateService.authorizationState();
    if (!property.propertyMembers?.length || !authState?.memberPermissions?.length) {
      return false;
    }

    const userMemberIds = authState.memberPermissions?.map(mp => mp.memberId) ?? [];
    return property.propertyMembers.some(pm =>
      pm.memberType === PropertyMemberTypes.Owner && userMemberIds.includes(pm.memberId)
    );
  }
}
